import { FC, useEffect, useState } from "react";
import "./ModalCouponsInsert.scss";
import ModalDefault from "../../../../components/ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Col, FormGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../../components/ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import ReactSelect from "react-select";
import { useCoupons } from "../../../../providers/Financial/Coupons/CouponsProvider";
import { customStyles } from "../../../../models/SelectCustomStyles";
import { useStatus } from "../../../../providers/Financial/Coupons/Status/StatusProvider";
import { CouponsModel } from "../../../../models/Financial/Coupons/CouponsModel";
import moment from "moment";
import FormatMoney from "../../../../utils/FormatMoney";

interface ModalCouponsInsertProps {
  show: boolean;
  onClose: () => void;
  coupon?: CouponsModel;
}

const ModalCouponsInsert: FC<ModalCouponsInsertProps> = ({
  show,
  onClose,
  coupon,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedStatus, setSelectedStatus] = useState<any>("");
  const [discountType, setDiscountType] = useState<string>("value");
  const [amount, setAmount] = useState<any>("");

  const { setError, handleFetch, handleList } = useCoupons();
  const { handleListStatus, statusOptions } = useStatus();
  const { onInvalid } = useAuth();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const clear = () => {
    reset({
      identifier: "",
      expiration_date: "",
      quantity_limit: 0,
      plan_coupon_status_id: 0,
      discount_type: "value",
      value: null,
      percent: null,
    });
    setSelectedStatus("");
    setDiscountType("value");
    setAmount("");
  };

  const handleAmount = (e: any) => {
    const inputValue: string = e.target.value.replace(/\D/g, "");
    const numericValue: number = parseFloat(inputValue) / 100;
    setAmount(numericValue);
    setValue("value", numericValue);
  };

  const handleClose = () => {
    setError("");
    clear();
    onClose();
  };

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);
    setError("");

    const ret = await handleFetch(data as any);

    if (ret) {
      toast.success("Cupom salvo com sucesso!");
      handleList();
      handleClose();
    }

    setIsLoadingSubmit(false);
  };

  useEffect(() => {
    if (show) {
      handleListStatus({
        page: 1,
        withPaginate: false,
        filter: { only_active: true },
      });
      if (coupon?.id) {
        setValue("id", coupon?.id);
        setValue("identifier", coupon?.identifier ?? "");
        setValue(
          "expiration_date",
          moment(coupon?.expiration_date).format("YYYY-MM-DD") ?? "",
        );
        setValue("plan_coupon_status_id", coupon?.plan_coupon_status_id ?? 0);
        setValue("quantity_limit", coupon?.quantity_limit ?? 0);
        setSelectedStatus(
          statusOptions.filter(
            (x: any) => x.value === coupon?.plan_coupon_status_id,
          ),
        );

        if (coupon?.value) {
          setDiscountType("value");
          setValue("discount_type", "value");
          setValue("value", coupon?.value);
          setAmount(coupon?.value);
          setValue("percent", null);
        } else if (coupon?.percent) {
          setDiscountType("percent");
          setValue("discount_type", "percent");
          setValue("percent", coupon?.percent);
          setValue("value", null);
        }
      } else {
        clear();
      }
    }
  }, [show]);

  return (
    <ModalDefault
      title={!!coupon?.id ? "Editar Cupom" : "Adicionar Cupom"}
      show={show}
      onClose={handleClose}
      sizeModal={"lg"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit || coupon?.deleted_at ? true : false}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalCouponsInsert" data-testid="ModalCouponsInsert">
        <Row>
          <Col md={7}>
            <FormGroup className="form-group">
              <label htmlFor="identifier">Identificador</label>
              <input
                className={`form-control ${onInvalid(errors?.identifier)}`}
                type="text"
                {...register("identifier")}
                onChange={(e: any) =>
                  setValue(
                    "identifier",
                    e?.target?.value.toUpperCase().replace(/[^A-Z0-9]/g, ""),
                  )
                }
                placeholder="Preencha aqui"
                maxLength={100}
                disabled={coupon?.deleted_at ? true : false}
              />
              <ErrorMessage
                name={"Identificador"}
                type={errors?.identifier?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={5}>
            <FormGroup className="form-group">
              <label htmlFor="expiration_date">Data de expiração</label>
              <input
                className={`form-control ${onInvalid(errors?.expiration_date)}`}
                type="date"
                {...register("expiration_date")}
                onChange={(e: any) =>
                  setValue("expiration_date", e?.target?.value)
                }
                disabled={coupon?.deleted_at ? true : false}
              />
              <ErrorMessage
                name={"Data de expiração"}
                type={errors?.expiration_date?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={7}>
            <FormGroup className="form-group">
              <label htmlFor="quantity_limit">Quantidade limite</label>
              <input
                className={`form-control ${onInvalid(errors?.quantity_limit)}`}
                type="number"
                {...register("quantity_limit")}
                onChange={(e: any) =>
                  setValue("quantity_limit", e?.target?.value)
                }
                placeholder="Preencha aqui"
                disabled={coupon?.deleted_at ? true : false}
              />
              <ErrorMessage
                name={"Quantidade limite"}
                type={errors?.quantity_limit?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={5}>
            <FormGroup className="form-group">
              <label htmlFor="plan_coupon_status_id">Status</label>
              <ReactSelect
                isSearchable
                options={statusOptions}
                value={selectedStatus ?? ""}
                defaultValue={selectedStatus}
                placeholder="Selecione..."
                className={`form-control p-0 ${onInvalid(errors?.plan_coupon_status_id)}`}
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(val: any) => {
                  setValue("plan_coupon_status_id", val?.value);
                  setSelectedStatus(val);
                }}
                isDisabled={coupon?.deleted_at ? true : false}
              />
              <ErrorMessage
                name={"Status"}
                type={errors?.plan_coupon_status_id?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={12} lg={6}>
            <FormGroup className="form-group">
              <label>Tipo de desconto</label>
              <div className="d-flex align-items-center gap-3 mt-1">
                <div className="form-check form-check-inline m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="discount_type_value"
                    value="value"
                    checked={discountType === "value"}
                    onChange={() => {
                      setDiscountType("value");
                      setValue("discount_type", "value");
                      setValue("percent", null);
                    }}
                    disabled={coupon?.deleted_at ? true : false}
                  />
                  <label className="form-check-label" htmlFor="discount_type_value">
                    Valor em Reais (R$)
                  </label>
                </div>
                <div className="form-check form-check-inline m-0">
                  <input
                    className="form-check-input"
                    type="radio"
                    id="discount_type_percent"
                    value="percent"
                    checked={discountType === "percent"}
                    onChange={() => {
                      setDiscountType("percent");
                      setValue("discount_type", "percent");
                      setValue("value", null);
                      setAmount("");
                    }}
                    disabled={coupon?.deleted_at ? true : false}
                  />
                  <label className="form-check-label" htmlFor="discount_type_percent">
                    Valor em Percentual (%)
                  </label>
                </div>
              </div>
              <ErrorMessage
                name={"Tipo de desconto"}
                type={errors?.discount_type?.type?.toString()}
              />
            </FormGroup>
          </Col>
          {discountType === "value" && (
            <Col md={12} lg={6}>
              <FormGroup className="form-group">
                <label htmlFor="value">Valor em Reais (R$)</label>
                <input
                  className={`form-control ${onInvalid(errors?.value)}`}
                  value={FormatMoney(amount || "")}
                  type="text"
                  onChange={(e: any) => handleAmount(e)}
                  placeholder="R$ 0,00"
                  disabled={coupon?.deleted_at ? true : false}
                />
                <ErrorMessage
                  name={"Valor em Reais"}
                  type={errors?.value?.type?.toString()}
                  isNumber
                />
              </FormGroup>
            </Col>
          )}
          {discountType === "percent" && (
            <Col md={12} lg={6}>
              <FormGroup className="form-group">
                <label htmlFor="percent">Valor em Percentual (%)</label>
                <input
                  className={`form-control ${onInvalid(errors?.percent)}`}
                  type="number"
                  step="0.01"
                  {...register("percent")}
                  onChange={(e: any) =>
                    setValue("percent", e?.target?.value)
                  }
                  placeholder="0"
                  min={0}
                  max={100}
                  disabled={coupon?.deleted_at ? true : false}
                />
                <ErrorMessage
                  name={"Valor em Percentual"}
                  type={errors?.percent?.type?.toString()}
                  max={100}
                  isNumber
                />
              </FormGroup>
            </Col>
          )}
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalCouponsInsert;
